import React from 'react';
import styled from 'styled-components';
import {
  Button, ButtonProps, Icon, IconType, Tooltip, Theme, HiveTooltipProps,
} from '@routific/hive';
import {
  Menu, Dropdown, MenuItemProps,
} from 'antd';
import { DropdownButtonProps } from 'antd/es/dropdown';
import type { V5ButtonHierarchy } from '@routific/hive/v5';
import ActionButtonV5 from './ActionButtonV5';

export interface Props {
  name: CommonActionTypes | string;
  type?: 'button' | 'menu' | 'dropdown' | 'textButton';
  tooltipProps?: HiveTooltipProps;
  disabled?: boolean;
  loading?: boolean;
  menuItemProps?: MenuItemProps;
  menuItemText?: string;
  buttonProps: ButtonProps;
  textButtonProps?: ButtonProps;
  dropdownButtonTitle?: string;
  dropdownProps?: DropdownButtonProps;
  // Opt into rendering a v5 Button (wrapped in a v5 Tooltip) instead of the
  // legacy hive Button. Applies to `button`, `menu`, and `textButton` types.
  v5?: boolean;
  // The icon node to show when `v5` is set (v5 icons are components, not the
  // legacy `IconType` enum).
  v5Icon?: React.ReactNode;
  // Visual weight for v5 text buttons. Ignored for icon-only / menu rows.
  v5Hierarchy?: V5ButtonHierarchy;
}

export enum ButtonTypes {
  BUTTON = 'button',
  MENU = 'menu',
  DROPDOWN = 'dropdown',
  TEXT_BUTTON = 'textButton',
}

export enum CommonActionTypes {
  DELETE = 'delete',
  MOVE = 'move',
  EDIT = 'edit',
  DOWNLOAD = 'download',
  REOPTIMIZE = 'reoptimize',
  DRAW_ROUTE = 'draw',
  REVERSE = 'reverse',
  PUBLISH = 'publish',
  UNPUBLISH = 'unpublish',
  DUPLICATE = 'duplicate',
  UNSCHEDULE = 'unschedule',
  SCHEDULE = 'schedule',
  USE_THESE_ORDERS = 'useTheseOrders',
  PREVIEW_SCHEDULE = 'previewSchedule',
  UPLOAD = 'upload',
  ADD_ORDER = 'addOrder',
  CREATE_ORDER_FROM_PROFILE = 'createOrderFromProfile',
  ADD_ROUTE = 'addRoute',
  ADD_ROUTE_FROM_TEMPLATE = 'addRouteFromTemplate',
  OPEN_PANEL = 'openPanel',
  CANCEL = 'cancel',
  UPGRADE = 'upgrade',
  BACK = 'back',
}

const StyledMenuItem = styled(Menu.Item)`
  &.ant-dropdown-menu-item {
    background-color: ${Theme.Colors.gray0};
  }

  // Override styles for disabled menu items
  &.ant-dropdown-menu-item.ant-dropdown-menu-item-disabled {
    background-color: ${Theme.Colors.gray0};
    color: ${Theme.Colors.textInvalid};
  }
  &.ant-dropdown-menu-item.ant-dropdown-menu-item-disabled:hover {
    background-color: ${Theme.Colors.gray1};
    color: ${Theme.Colors.textInvalid};
  }
`;

const ActionButton: React.FC<Props> = ({
  name, type = 'button', tooltipProps, disabled, loading, menuItemProps, menuItemText, buttonProps, textButtonProps, dropdownButtonTitle, dropdownProps, v5, v5Icon, v5Hierarchy,
}) => {
  const dataCy = `${name}-btn`;

  if (v5 && (type === ButtonTypes.BUTTON || type === ButtonTypes.MENU || type === ButtonTypes.TEXT_BUTTON)) {
    return (
      <ActionButtonV5
        type={type}
        tooltipProps={tooltipProps}
        disabled={disabled}
        loading={loading}
        menuItemProps={menuItemProps}
        menuItemText={menuItemText}
        buttonProps={buttonProps}
        textButtonProps={textButtonProps}
        icon={v5Icon}
        dataCy={dataCy}
        hierarchy={v5Hierarchy}
      />
    );
  }

  const getButton = () => {
    switch (type) {
      case 'menu':
        return (
          <StyledMenuItem
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            data-cy={dataCy}
            data-testid={dataCy}
            key={name}
            {...menuItemProps}
          >
            <Tooltip
              placement="right"
              title=""
              {...(tooltipProps || {})}
            >
              <div className="text-wrapper">
                {menuItemText}
              </div>
            </Tooltip>
          </StyledMenuItem>
        );
      case 'dropdown':
        return (
          <Dropdown.Button
            overlay={dropdownProps?.overlay ?? <></>}
            icon={<Icon icon={IconType.ExpandDown} />}
            data-cy={dataCy}
            data-testid={dataCy}
            overlayClassName="dropdown-menu"
            trigger={['click']}
            placement="topRight"
            disabled={disabled}
            {...dropdownProps}
          >
            {dropdownButtonTitle}
          </Dropdown.Button>
        );
      case 'textButton':
        return (
          <Button
            disabled={disabled}
            loading={loading}
            data-cy={dataCy}
            data-testid={dataCy}
            {...textButtonProps}
          >
            {textButtonProps?.title ?? ''}
          </Button>
        );
      default:
        return (
          <Button
            icon={IconType.Reoptimize}
            data-cy={dataCy}
            data-testid={dataCy}
            type="link"
            size="small"
            disabled={disabled}
            style={{ pointerEvents: disabled ? 'none' : 'auto', ...buttonProps.style }}
            {...buttonProps}
          />
        );
    }
  };

  if (tooltipProps && type !== ButtonTypes.MENU) {
    return (
      <Tooltip
        data-testid="tooltip"
        disabled={disabled}
        placement={tooltipProps?.placement ?? 'top'}
        mouseLeaveDelay={0}
        {...tooltipProps}
      >
        {getButton()}
      </Tooltip>
    );
  }

  return (
    <>
      {getButton()}
    </>
  );
};

export default ActionButton;
